"use client";

import { c as _c } from "react/compiler-runtime";
import NFTMarketplaceLinks from "@/components/nft-marketplace-links/NFTMarketplaceLinks";
import { MemePageArtViewer } from "@/components/the-memes/MemePageArtViewer";
import { getSafeExternalUrl } from "@/components/the-memes/MemePageAdditionalDetails";
import { MemeArtworkDetails } from "@/components/the-memes/MemePageLiveStats";
import type { LabExtendedData, LabNFT } from "@/entities/INFT";
import { addProtocol, numberWithCommas } from "@/helpers/Helpers";
import { ChartBarSquareIcon, LinkIcon } from "@heroicons/react/24/outline";
import { faFire } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import clsx from "clsx";
import Link from "next/link";
import type { ReactNode } from "react";
const MEME_LAB_LABEL_CLASS = "tw-mb-1 tw-text-sm tw-font-medium tw-leading-5 tw-text-iron-400";
const MEME_LAB_VALUE_CLASS = "tw-text-sm tw-font-semibold tw-leading-5 tw-text-white md:tw-text-lg md:tw-leading-6";
const MEME_LAB_MARKET_GRID_CLASS = "tw-grid tw-grid-cols-1 tw-gap-x-8 tw-gap-y-6 sm:tw-grid-cols-2 xl:tw-grid-cols-3";
const MEME_LAB_SECTION_TITLE_CLASS = "tw-mb-4 tw-text-xs tw-font-semibold tw-uppercase tw-leading-4 tw-text-iron-400";
export const MEME_LAB_STATS_ROW_CLASS = "tw-flex tw-flex-wrap tw-items-start tw-gap-x-6 tw-gap-y-6 md:tw-gap-x-10";
const trimToEmpty = (value: unknown): string => typeof value === "string" ? value.trim() : "";
const getMemeLabWebsiteUrls = (value: unknown): string[] => trimToEmpty(value).split(/[\s,]+/).map(website => website.trim()).filter(website => website.length > 0);
function formatMemeLabMarketValue(value: number, decimals = 1000) {
  if (value <= 0) {
    return "N/A";
  }
  return numberWithCommas(Math.round(value * decimals) / decimals);
}
function MemeLabInfoItem(t0) {
  const $ = _c(7);
  const {
    label,
    children
  } = t0;
  let t1;
  if ($[0] !== label) {
    t1 = <div className="tw-mb-1 md:tw-mb-2"><span className={MEME_LAB_LABEL_CLASS}>{label}</span></div>;
    $[0] = label;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== children) {
    t2 = <div className={MEME_LAB_VALUE_CLASS}>{children}</div>;
    $[2] = children;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== t1 || $[5] !== t2) {
    t3 = <div className="tw-min-w-[8.5rem]">{t1}{t2}</div>;
    $[4] = t1;
    $[5] = t2;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  return t3;
}
export function MemeLabStatMetric(t0) {
  const $ = _c(16);
  const {
    label,
    value,
    rank,
    total,
    icon
  } = t0;
  let t1;
  if ($[0] !== label) {
    t1 = <span className={MEME_LAB_LABEL_CLASS}>{label}</span>;
    $[0] = label;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== icon || $[3] !== t1) {
    t2 = <div className="tw-mb-1 tw-flex tw-items-center tw-gap-2 md:tw-mb-2">{icon}{t1}</div>;
    $[2] = icon;
    $[3] = t1;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] !== value) {
    t3 = <span className={MEME_LAB_VALUE_CLASS}>{value}</span>;
    $[5] = value;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] !== rank || $[8] !== total) {
    t4 = rank !== undefined && total !== undefined && <span className="tw-inline-flex tw-items-center tw-rounded-full tw-border tw-border-solid tw-border-iron-800 tw-px-2 tw-py-1 tw-text-[10px] tw-font-semibold tw-leading-none tw-text-iron-400 md:tw-py-0.5 md:tw-text-[11px] md:tw-leading-4">Rank {numberWithCommas(rank)}/{numberWithCommas(total)}</span>;
    $[7] = rank;
    $[8] = total;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  let t5;
  if ($[10] !== t3 || $[11] !== t4) {
    t5 = <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-x-2 tw-gap-y-1.5">{t3}{t4}</div>;
    $[10] = t3;
    $[11] = t4;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  let t6;
  if ($[13] !== t2 || $[14] !== t5) {
    t6 = <div className="tw-min-w-[8.5rem]">{t2}{t5}</div>;
    $[13] = t2;
    $[14] = t5;
    $[15] = t6;
  } else {
    t6 = $[15];
  }
  return t6;
}
function MemeLabMarketMetric(t0) {
  const $ = _c(16);
  const {
    label,
    value,
    decimals,
    unit
  } = t0;
  let t1;
  if ($[0] !== decimals || $[1] !== value) {
    t1 = formatMemeLabMarketValue(value, decimals);
    $[0] = decimals;
    $[1] = value;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const formattedValue = t1;
  let t2;
  if ($[3] !== label) {
    t2 = <div className={MEME_LAB_LABEL_CLASS}>{label}</div>;
    $[3] = label;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] !== formattedValue) {
    t3 = <span className={MEME_LAB_VALUE_CLASS}>{formattedValue}</span>;
    $[5] = formattedValue;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] !== formattedValue || $[8] !== unit) {
    t4 = unit && formattedValue !== "N/A" && <span className="tw-ml-1.5 tw-text-sm tw-font-medium tw-leading-none tw-text-iron-400">{unit}</span>;
    $[7] = formattedValue;
    $[8] = unit;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  let t5;
  if ($[10] !== t3 || $[11] !== t4) {
    t5 = <div className="tw-flex tw-items-baseline">{t3}{t4}</div>;
    $[10] = t3;
    $[11] = t4;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  let t6;
  if ($[13] !== t2 || $[14] !== t5) {
    t6 = <div className="tw-min-w-[8.5rem]">{t2}{t5}</div>;
    $[13] = t2;
    $[14] = t5;
    $[15] = t6;
  } else {
    t6 = $[15];
  }
  return t6;
}
function MemeLabMetadataLink(t0) {
  const $ = _c(5);
  const {
    url
  } = t0;
  let t1;
  if ($[0] !== url) {
    const metadataUrl = trimToEmpty(url);
    t1 = getSafeExternalUrl(metadataUrl);
    $[0] = url;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const safeMetadataUrl = t1;
  if (!safeMetadataUrl) {
    return null;
  }
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <div className={MEME_LAB_LABEL_CLASS}>Metadata</div>;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== safeMetadataUrl) {
    t3 = <div className="tw-min-w-[8.5rem]">{t2}<Link href={safeMetadataUrl} target="_blank" rel="noopener noreferrer" className="tw-text-sm tw-font-semibold tw-leading-5 tw-text-white tw-no-underline hover:tw-text-iron-300 md:tw-text-lg md:tw-leading-6">View</Link></div>;
    $[3] = safeMetadataUrl;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  return t3;
}
function MemeLabDistributionPlanLink(t0) {
  const $ = _c(2);
  const {
    nft
  } = t0;
  if (!nft.has_distribution) {
    return null;
  }
  const t1 = `/meme-lab/${nft.id}/distribution`;
  let t2;
  if ($[0] !== t1) {
    t2 = <section className="tw-pt-6"><Link href={t1} className="tw-inline-flex tw-items-center tw-rounded-md tw-bg-iron-900 tw-px-4 tw-py-2 tw-text-xs tw-font-semibold tw-text-iron-300 tw-no-underline tw-transition-colors hover:tw-bg-iron-800 hover:tw-text-white">Distribution Plan</Link></section>;
    $[0] = t1;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  return t2;
}
export function MemeLabStaticCardHeader(t0) {
  const $ = _c(13);
  const {
    nft,
    nftMeta,
    showMarketplaceLinks,
    artworkFooter
  } = t0;
  const t1 = `${nft.contract}-${nft.id}`;
  let t2;
  if ($[0] !== nft || $[1] !== t1) {
    t2 = <div className="tw-flex tw-min-w-0 tw-items-center tw-pb-5 tw-pt-2 lg:tw-flex-1"><MemePageArtViewer key={t1} nft={nft} showBalance={true} /></div>;
    $[0] = nft;
    $[1] = t1;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== artworkFooter || $[4] !== t2) {
    t3 = <div className="tw-relative lg:tw-flex lg:tw-flex-col lg:tw-self-stretch">{t2}{artworkFooter}</div>;
    $[3] = artworkFooter;
    $[4] = t2;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== nft || $[7] !== nftMeta || $[8] !== showMarketplaceLinks) {
    t4 = <div className="tw-pt-6 md:tw-pt-8 lg:tw-pt-2"><MemeLabLiveDetails nft={nft} nftMeta={nftMeta} showMarketplaceLinks={showMarketplaceLinks} /></div>;
    $[6] = nft;
    $[7] = nftMeta;
    $[8] = showMarketplaceLinks;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  let t5;
  if ($[10] !== t3 || $[11] !== t4) {
    t5 = <div className="tw-mb-6 tw-grid tw-grid-cols-1 tw-gap-x-10 lg:tw-grid-cols-[minmax(0,11fr)_minmax(0,9fr)] xl:tw-gap-x-16">{t3}{t4}</div>;
    $[10] = t3;
    $[11] = t4;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  return t5;
}
function MemeLabLiveDetails(t0) {
  const $ = _c(35);
  const {
    nft,
    nftMeta,
    showMarketplaceLinks
  } = t0;
  let t1;
  if ($[0] !== nft) {
    t1 = <MemeArtworkDetails nft={nft} layout="aligned" />;
    $[0] = nft;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== nftMeta) {
    t2 = <section aria-label="Card details" className="tw-border-x-0 tw-border-b tw-border-t-0 tw-border-solid tw-border-iron-800 tw-py-6 md:tw-py-8"><MemeLabCardDetailsStats nftMeta={nftMeta} /></section>;
    $[2] = nftMeta;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <h3 className={MEME_LAB_SECTION_TITLE_CLASS}>Market Overview</h3>;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== nft.mint_price) {
    t4 = <MemeLabMarketMetric label="Mint Price" value={nft.mint_price} decimals={100000} unit="ETH" />;
    $[5] = nft.mint_price;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] !== nft.uri) {
    t5 = <MemeLabMetadataLink url={nft.uri} />;
    $[7] = nft.uri;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  let t6;
  if ($[9] !== nft.floor_price) {
    t6 = <MemeLabMarketMetric label="Floor Price" value={nft.floor_price} unit="ETH" />;
    $[9] = nft.floor_price;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  let t7;
  if ($[11] !== nft.market_cap) {
    t7 = <MemeLabMarketMetric label="Market Cap" value={nft.market_cap} decimals={100} unit="ETH" />;
    $[11] = nft.market_cap;
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  let t8;
  if ($[13] !== nft.highest_offer) {
    t8 = <MemeLabMarketMetric label="Highest Offer" value={nft.highest_offer} unit="ETH" />;
    $[13] = nft.highest_offer;
    $[14] = t8;
  } else {
    t8 = $[14];
  }
  let t9;
  if ($[15] !== nft.contract || $[16] !== nft.id || $[17] !== showMarketplaceLinks) {
    t9 = showMarketplaceLinks && <div className="tw-flex tw-min-w-[8.5rem] tw-items-end"><NFTMarketplaceLinks contract={nft.contract} id={nft.id} /></div>;
    $[15] = nft.contract;
    $[16] = nft.id;
    $[17] = showMarketplaceLinks;
    $[18] = t9;
  } else {
    t9 = $[18];
  }
  let t10;
  if ($[19] !== t4 || $[20] !== t5 || $[21] !== t6 || $[22] !== t7 || $[23] !== t8 || $[24] !== t9) {
    t10 = <div className={MEME_LAB_MARKET_GRID_CLASS}>{t4}{t5}{t6}{t7}{t8}{t9}</div>;
    $[19] = t4;
    $[20] = t5;
    $[21] = t6;
    $[22] = t7;
    $[23] = t8;
    $[24] = t9;
    $[25] = t10;
  } else {
    t10 = $[25];
  }
  let t11;
  if ($[26] !== nft) {
    t11 = <MemeLabDistributionPlanLink nft={nft} />;
    $[26] = nft;
    $[27] = t11;
  } else {
    t11 = $[27];
  }
  let t12;
  if ($[28] !== t10 || $[29] !== t11) {
    t12 = <section className="tw-pt-6 md:tw-pt-8">{t3}{t10}{t11}</section>;
    $[28] = t10;
    $[29] = t11;
    $[30] = t12;
  } else {
    t12 = $[30];
  }
  let t13;
  if ($[31] !== t1 || $[32] !== t12 || $[33] !== t2) {
    t13 = <div className="tw-w-full">{t1}{t2}{t12}</div>;
    $[31] = t1;
    $[32] = t12;
    $[33] = t2;
    $[34] = t13;
  } else {
    t13 = $[34];
  }
  return t13;
}
function MemeLabCardDetailsStats(t0) {
  const $ = _c(34);
  const {
    nftMeta
  } = t0;
  let t1;
  if ($[0] !== nftMeta.burnt || $[1] !== nftMeta.collection_size || $[2] !== nftMeta.edition_size || $[3] !== nftMeta.edition_size_cleaned || $[4] !== nftMeta.edition_size_cleaned_rank || $[5] !== nftMeta.edition_size_not_burnt || $[6] !== nftMeta.edition_size_not_burnt_rank || $[7] !== nftMeta.edition_size_rank || $[8] !== nftMeta.metadata_collection || $[9] !== nftMeta.website) {
    const websiteUrls = getMemeLabWebsiteUrls(nftMeta.website);
    let t2;
    if ($[11] !== nftMeta.edition_size) {
      t2 = numberWithCommas(nftMeta.edition_size);
      $[11] = nftMeta.edition_size;
      $[12] = t2;
    } else {
      t2 = $[12];
    }
    let t3;
    if ($[13] !== nftMeta.collection_size || $[14] !== nftMeta.edition_size_rank || $[15] !== t2) {
      t3 = <MemeLabStatMetric label="Edition Size" value={t2} rank={nftMeta.edition_size_rank} total={nftMeta.collection_size} />;
      $[13] = nftMeta.collection_size;
      $[14] = nftMeta.edition_size_rank;
      $[15] = t2;
      $[16] = t3;
    } else {
      t3 = $[16];
    }
    let t4;
    if ($[17] !== nftMeta.burnt || $[18] !== nftMeta.collection_size || $[19] !== nftMeta.edition_size_not_burnt || $[20] !== nftMeta.edition_size_not_burnt_rank) {
      t4 = nftMeta.burnt > 0 && <><MemeLabStatMetric label="Burnt" value={numberWithCommas(nftMeta.burnt)} icon={<FontAwesomeIcon icon={faFire} className="tw-h-4 tw-w-4 tw-text-red" />} /><MemeLabStatMetric label="Edition Size ex. Burnt" value={numberWithCommas(nftMeta.edition_size_not_burnt)} rank={nftMeta.edition_size_not_burnt_rank} total={nftMeta.collection_size} /></>;
      $[17] = nftMeta.burnt;
      $[18] = nftMeta.collection_size;
      $[19] = nftMeta.edition_size_not_burnt;
      $[20] = nftMeta.edition_size_not_burnt_rank;
      $[21] = t4;
    } else {
      t4 = $[21];
    }
    const t5 = `ex.${nftMeta.burnt > 0 ? " Burnt and" : ""} 6529 Museum`;
    let t6;
    if ($[22] !== nftMeta.edition_size_cleaned) {
      t6 = numberWithCommas(nftMeta.edition_size_cleaned);
      $[22] = nftMeta.edition_size_cleaned;
      $[23] = t6;
    } else {
      t6 = $[23];
    }
    let t7;
    if ($[24] !== nftMeta.collection_size || $[25] !== nftMeta.edition_size_cleaned_rank || $[26] !== t5 || $[27] !== t6) {
      t7 = <MemeLabStatMetric label={t5} value={t6} rank={nftMeta.edition_size_cleaned_rank} total={nftMeta.collection_size} />;
      $[24] = nftMeta.collection_size;
      $[25] = nftMeta.edition_size_cleaned_rank;
      $[26] = t5;
      $[27] = t6;
      $[28] = t7;
    } else {
      t7 = $[28];
    }
    let t8;
    if ($[29] !== nftMeta.metadata_collection) {
      t8 = nftMeta.metadata_collection.replaceAll(" ", "-");
      $[29] = nftMeta.metadata_collection;
      $[30] = t8;
    } else {
      t8 = $[30];
    }
    const t9 = `/meme-lab/collection/${encodeURIComponent(t8)}`;
    let t10;
    if ($[31] !== nftMeta.metadata_collection || $[32] !== t9) {
      t10 = <MemeLabInfoItem label="Collection"><Link href={t9} className="tw-text-white tw-no-underline hover:tw-text-iron-300">{nftMeta.metadata_collection}</Link></MemeLabInfoItem>;
      $[31] = nftMeta.metadata_collection;
      $[32] = t9;
      $[33] = t10;
    } else {
      t10 = $[33];
    }
    t1 = <div className={MEME_LAB_MARKET_GRID_CLASS}>{t3}{t4}{t7}{t10}{websiteUrls.length > 0 && <div className="tw-min-w-0 sm:tw-col-span-2 xl:tw-col-span-3"><MemeLabInfoItem label="Website"><span className="tw-flex tw-min-w-0 tw-flex-col tw-items-start tw-gap-2">{websiteUrls.map(_temp)}</span></MemeLabInfoItem></div>}</div>;
    $[0] = nftMeta.burnt;
    $[1] = nftMeta.collection_size;
    $[2] = nftMeta.edition_size;
    $[3] = nftMeta.edition_size_cleaned;
    $[4] = nftMeta.edition_size_cleaned_rank;
    $[5] = nftMeta.edition_size_not_burnt;
    $[6] = nftMeta.edition_size_not_burnt_rank;
    $[7] = nftMeta.edition_size_rank;
    $[8] = nftMeta.metadata_collection;
    $[9] = nftMeta.website;
    $[10] = t1;
  } else {
    t1 = $[10];
  }
  return t1;
}
function _temp(website) {
  return <Link key={`meta-website-${website}`} href={addProtocol(website)} target="_blank" rel="noopener noreferrer" className="tw-inline-flex tw-w-fit tw-max-w-full tw-items-start tw-gap-2 tw-text-white tw-no-underline hover:tw-text-iron-300"><LinkIcon className="tw-mt-1 tw-h-4 tw-w-4 tw-flex-shrink-0 tw-text-iron-500" /><span className="tw-min-w-0 tw-break-words">{website}</span></Link>;
}
export function MemeLabCardVolumes(t0) {
  const $ = _c(16);
  const {
    nft
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <ChartBarSquareIcon className="tw-h-4 tw-w-4 tw-flex-shrink-0 tw-text-iron-500" />;
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  let t3;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <div className="tw-flex tw-items-center tw-gap-3">{t1}<h3 id="meme-lab-card-volumes-heading" className={clsx(MEME_LAB_SECTION_TITLE_CLASS, "tw-mb-0")}>Card Volumes</h3><div className="tw-h-px tw-min-w-10 tw-flex-grow tw-bg-gradient-to-r tw-from-iron-700 tw-to-transparent" /></div>;
    t3 = clsx("tw-mt-6", MEME_LAB_STATS_ROW_CLASS);
    $[1] = t2;
    $[2] = t3;
  } else {
    t2 = $[1];
    t3 = $[2];
  }
  let t4;
  if ($[3] !== nft.total_volume_last_24_hours) {
    t4 = <MemeLabMarketMetric label="24 Hours" value={nft.total_volume_last_24_hours} decimals={100} unit="ETH" />;
    $[3] = nft.total_volume_last_24_hours;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  let t5;
  if ($[5] !== nft.total_volume_last_7_days) {
    t5 = <MemeLabMarketMetric label="7 Days" value={nft.total_volume_last_7_days} decimals={100} unit="ETH" />;
    $[5] = nft.total_volume_last_7_days;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  let t6;
  if ($[7] !== nft.total_volume_last_1_month) {
    t6 = <MemeLabMarketMetric label="30 Days" value={nft.total_volume_last_1_month} decimals={100} unit="ETH" />;
    $[7] = nft.total_volume_last_1_month;
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  let t7;
  if ($[9] !== nft.total_volume) {
    t7 = <MemeLabMarketMetric label="All Time" value={nft.total_volume} decimals={100} unit="ETH" />;
    $[9] = nft.total_volume;
    $[10] = t7;
  } else {
    t7 = $[10];
  }
  let t8;
  if ($[11] !== t4 || $[12] !== t5 || $[13] !== t6 || $[14] !== t7) {
    t8 = <section aria-labelledby="meme-lab-card-volumes-heading">{t2}<div className={t3}>{t4}{t5}{t6}{t7}</div></section>;
    $[11] = t4;
    $[12] = t5;
    $[13] = t6;
    $[14] = t7;
    $[15] = t8;
  } else {
    t8 = $[15];
  }
  return t8;
}